import { createBaseExportSchema } from '@app/modules/social-export/components/validation/useBaseExportSchema';
import { useMemo } from 'react';
import { IntlFormatters, useIntl } from 'react-intl';
import { z } from 'zod';

const YOUTUBE_TITLE_MAX_LENGTH = 100;
const YOUTUBE_DESCRIPTION_MAX_LENGTH = 5000;

const youtubeTitleForbiddenCharsMsg = {
  id: 'socialExport.validation.youtubeTitleForbiddenChars',
  defaultMessage: 'El título no puede contener los caracteres < o >',
};

const maxPlaylistsMsg = {
  id: 'socialExport.validation.maxPlaylists',
  defaultMessage: 'Maximum 10 playlists allowed',
};

/** Builds the Zod schema for the YouTube publish form. */
export function createYoutubePublishSchema(t: IntlFormatters['$t']) {
  const youtubeSettingsSchema = z.object({
    language: z
      .string()
      .nullable()
      .optional()
      .transform((val) => (val === '' ? null : val)),
    privacyStatus: z.string().min(1),
    madeForKids: z.boolean(),
    categoryId: z
      .string()
      .nullable()
      .optional()
      .transform((val) => (val === '' ? null : val)),
  });

  return createBaseExportSchema(t)
    .merge(youtubeSettingsSchema)
    .extend({
      title: z
        .string()
        .trim()
        .min(1)
        .max(YOUTUBE_TITLE_MAX_LENGTH)
        .refine((value) => !/[<>]/.test(value), {
          message: t(youtubeTitleForbiddenCharsMsg),
        }),
      content: z.string().trim().min(1).max(YOUTUBE_DESCRIPTION_MAX_LENGTH),
      tags: z.string(),
      thumbnailUrl: z.string().optional(),
      thumbType: z.enum(['DEFAULT', 'IMAGE_URL', 'VIDEO_FRAME']),
      thumbOffsetMs: z.number().nullable().optional(),
      playlistsIds: z.array(z.any()).max(10, t(maxPlaylistsMsg)).nullable().optional(),
    })
    .refine(
      (data) => {
        if (data.thumbType === 'IMAGE_URL') {
          return !!data.thumbnailUrl;
        }
        return true;
      },
      { path: ['thumbnailUrl'] },
    )
    .refine(
      (data) => {
        if (data.thumbType === 'VIDEO_FRAME') {
          return data.thumbOffsetMs !== null && data.thumbOffsetMs !== undefined;
        }
        return true;
      },
      { path: ['thumbOffsetMs'] },
    );
}

export type YoutubePublishValues = z.infer<ReturnType<typeof createYoutubePublishSchema>>;

/** Zod schema for the YouTube publish form with translated validation messages. */
export function useYoutubePublishSchema() {
  const { $t } = useIntl();
  return useMemo(() => createYoutubePublishSchema($t), [$t]);
}
